import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './ClientProfile.css';

/* ─── Helpers ─── */

function calcularIdade(nascimento) {
  if (!nascimento) return null;
  const partes = nascimento.split('/');
  if (partes.length < 2) return null;
  const ano = parseInt(partes[partes.length - 1]);
  const mes = parseInt(partes[partes.length === 3 ? 1 : 0]) - 1;
  const dia = partes.length === 3 ? parseInt(partes[0]) : 1;
  if (isNaN(ano) || ano < 1900 || ano > 2100) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - ano;
  if (hoje.getMonth() < mes || (hoje.getMonth() === mes && hoje.getDate() < dia)) {
    idade--;
  }
  return idade > 0 ? idade : null;
}

function labelRelacao(rel) {
  const map = {
    esposa: 'Esposa', marido: 'Marido', esposo: 'Esposo',
    filho: 'Filho', filha: 'Filha',
    pai: 'Pai', mae: 'Mãe', mãe: 'Mãe',
    irmao: 'Irmão', irmã: 'Irmã', irmão: 'Irmão',
  };
  return map[rel?.toLowerCase()] || rel;
}

/* ─── Subcomponents ─── */

function Alerta({ texto, editMode, onChange }) {
  if (!editMode && !texto) return null;
  if (editMode) {
    return (
      <div className="cp-section">
        <span className="cp-label">⚠️ Alerta</span>
        <textarea
          className="cp-edit-textarea"
          value={texto || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Pendência financeira, conflito ou situação sensível..."
          rows={2}
        />
      </div>
    );
  }
  return (
    <div className="cp-alerta">
      <span className="cp-alerta-icon">⚠️</span>
      <p>{texto}</p>
    </div>
  );
}

function ComoAgir({ texto, editMode, onChange }) {
  if (!editMode && !texto) return null;
  if (editMode) {
    return (
      <div className="cp-section">
        <span className="cp-label">Como agir</span>
        <textarea
          className="cp-edit-textarea"
          value={texto || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Como o vendedor deve agir com esse cliente..."
          rows={3}
        />
      </div>
    );
  }
  return (
    <div className="cp-section">
      <span className="cp-label">Como agir</span>
      <p className="cp-como-agir">{texto}</p>
    </div>
  );
}

function Falas({ falas, editMode, onChange }) {
  if (!editMode && !falas?.length) return null;

  if (editMode) {
    const lista = falas?.length ? falas : [{ contexto: '', exemplo: '' }];
    const update = (idx, field, value) => {
      const nova = lista.map((f, i) => i === idx ? { ...f, [field]: value } : f);
      onChange(nova);
    };
    const adicionar = () => onChange([...lista, { contexto: '', exemplo: '' }]);
    const remover = (idx) => onChange(lista.filter((_, i) => i !== idx));

    return (
      <div className="cp-section">
        <span className="cp-label">Como eu falo com ele</span>
        {lista.map((f, i) => (
          <div key={i} className="cp-edit-fala">
            <input
              className="cp-edit-input"
              value={f.contexto}
              onChange={e => update(i, 'contexto', e.target.value)}
              placeholder="Contexto (ex: Tratamento padrão)"
            />
            <input
              className="cp-edit-input"
              value={f.exemplo}
              onChange={e => update(i, 'exemplo', e.target.value)}
              placeholder="Frase exemplo"
            />
            <button className="cp-edit-remove" onClick={() => remover(i)}>✕</button>
          </div>
        ))}
        <button className="cp-edit-add" onClick={adicionar}>+ Adicionar fala</button>
      </div>
    );
  }

  return (
    <div className="cp-section">
      <span className="cp-label">Como eu falo com ele</span>
      <div className="cp-falas">
        {falas.map((f, i) => (
          <div key={i} className="cp-fala">
            <span className="cp-fala-contexto">{f.contexto}</span>
            <p className="cp-fala-exemplo">"{f.exemplo}"</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Familia({ membros, editMode, onChange }) {
  const validos = (membros || []).filter(m => m.relacao);
  if (!editMode && !validos.length) return null;

  if (editMode) {
    const lista = membros?.length ? membros : [{ relacao: '', nome: '', nascimento: '' }];
    const update = (idx, field, value) => {
      const nova = lista.map((m, i) => i === idx ? { ...m, [field]: value } : m);
      onChange(nova);
    };
    const adicionar = () => onChange([...lista, { relacao: '', nome: '', nascimento: '' }]);
    const remover = (idx) => onChange(lista.filter((_, i) => i !== idx));

    return (
      <div className="cp-section">
        <span className="cp-label">Família</span>
        {lista.map((m, i) => (
          <div key={i} className="cp-edit-fala">
            <input
              className="cp-edit-input"
              value={m.relacao}
              onChange={e => update(i, 'relacao', e.target.value)}
              placeholder="Relação (esposa, filho...)"
            />
            <input
              className="cp-edit-input"
              value={m.nome || ''}
              onChange={e => update(i, 'nome', e.target.value)}
              placeholder="Nome"
            />
            <input
              className="cp-edit-input"
              value={m.nascimento || ''}
              onChange={e => update(i, 'nascimento', e.target.value)}
              placeholder="Nascimento DD/MM/AAAA"
            />
            <button className="cp-edit-remove" onClick={() => remover(i)}>✕</button>
          </div>
        ))}
        <button className="cp-edit-add" onClick={adicionar}>+ Adicionar</button>
      </div>
    );
  }

  return (
    <div className="cp-section">
      <span className="cp-label">Família</span>
      <div className="cp-familia">
        {validos.map((m, i) => {
          const idade = calcularIdade(m.nascimento);
          return (
            <div key={i} className="cp-familiar">
              <span className="cp-familiar-relacao">{labelRelacao(m.relacao)}</span>
              {m.nome && <span className="cp-familiar-nome">{m.nome}</span>}
              {idade && <span className="cp-familiar-idade">{idade} anos</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function parseDataCompra(data) {
  if (!data) return 0;
  const partes = data.split('/');
  if (partes.length === 2) return parseInt(partes[1]) * 100 + parseInt(partes[0]);
  return 0;
}

function Compras({ historico }) {
  if (!historico?.length) return null;
  const ordenado = [...historico].sort((a, b) => parseDataCompra(b.data) - parseDataCompra(a.data));
  return (
    <div className="cp-section">
      <span className="cp-label">Últimas compras</span>
      <div className="cp-compras">
        {ordenado.slice(0, 4).map((c, i) => (
          <div key={i} className="cp-compra">
            <div className="cp-compra-info">
              <span className="cp-compra-desc">{c.descricao}</span>
              <span className="cp-compra-data">{c.data}</span>
            </div>
            {c.valor && <span className="cp-compra-valor">{c.valor}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Contexto({ tags, editMode, onChange }) {
  if (!editMode && !tags?.length) return null;

  if (editMode) {
    const [novaTag, setNovaTag] = useState('');
    const lista = tags || [];
    const adicionar = () => {
      const t = novaTag.trim();
      if (t && !lista.includes(t)) onChange([...lista, t]);
      setNovaTag('');
    };
    const remover = (idx) => onChange(lista.filter((_, i) => i !== idx));
    return (
      <div className="cp-section cp-section-tags">
        <span className="cp-label">Contexto</span>
        <div className="cp-tags">
          {lista.map((t, i) => (
            <span key={i} className="cp-tag cp-tag-edit">
              {t.replace(/_/g, ' ')}
              <button className="cp-tag-remove" onClick={() => remover(i)}>✕</button>
            </span>
          ))}
        </div>
        <div className="cp-edit-tag-row">
          <input
            className="cp-edit-input"
            value={novaTag}
            onChange={e => setNovaTag(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && adicionar()}
            placeholder="Nova tag..."
          />
          <button className="cp-edit-add-inline" onClick={adicionar}>+</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-section cp-section-tags">
      <span className="cp-label">Contexto</span>
      <div className="cp-tags">
        {tags.map((t, i) => (
          <span key={i} className="cp-tag">{t.replace(/_/g, ' ')}</span>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Component ─── */

function ClientProfile({ contactId }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blocking, setBlocking] = useState(false);

  // Edição manual
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const fileInputRef = React.useRef(null);

  const carregarPerfil = useCallback(() => {
    if (!contactId) return;
    setLoading(true);
    setError(null);

    api.getProfile(contactId)
      .then(res => {
        const p = res.exists ? res.profile : null;
        setProfile(p);

        // Auto-geração: se não existe perfil, gera automaticamente
        if (!res.exists) {
          setGenerating(true);
          const formData = new FormData();
          formData.append('trigger', 'auto');
          fetch(`/api/crm/profiles/contact/${contactId}/generate`, {
            method: 'POST', body: formData
          })
            .then(r => r.ok ? r.json() : null)
            .then(() => {
              // Recarrega após gerar
              return api.getProfile(contactId);
            })
            .then(res2 => {
              if (res2.exists) setProfile(res2.profile);
            })
            .catch(err => console.warn('[perfil] Auto-geração falhou:', err))
            .finally(() => setGenerating(false));
        }
      })
      .catch(err => {
        console.error('Erro ao carregar perfil:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [contactId]);

  const carregarContato = useCallback(() => {
    if (!contactId) return;
    api.get(`/contacts/${contactId}`)
      .then(res => setIsBlocked(res.is_blocked || false))
      .catch(() => {});
  }, [contactId]);

  useEffect(() => {
    carregarPerfil();
    carregarContato();
  }, [carregarPerfil, carregarContato]);

  const handleGerar = async () => {
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('trigger', 'manual');
      arquivos.forEach(f => formData.append('files', f));

      const res = await fetch(
        `/api/crm/profiles/contact/${contactId}/generate`,
        { method: 'POST', body: formData }
      );
      if (res.status === 429) {
        const data = await res.json();
        alert(data.error || 'Perfil gerado recentemente. Aguarde para atualizar.');
        return;
      }
      if (!res.ok) throw new Error('Erro ao gerar perfil');
      setArquivos([]);
      await carregarPerfil();
    } catch (err) {
      console.error('Erro ao gerar perfil:', err);
      alert('Erro ao gerar perfil. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleBlock = async () => {
    setBlocking(true);
    try {
      const endpoint = isBlocked
        ? `/contacts/${contactId}/unblock`
        : `/contacts/${contactId}/block`;
      const res = await api.patch(endpoint);
      setIsBlocked(res.is_blocked);
    } catch (err) {
      console.error('Erro ao bloquear/desbloquear contato:', err);
      alert('Erro ao atualizar bloqueio. Tente novamente.');
    } finally {
      setBlocking(false);
    }
  };

  const handleArquivos = (e) => {
    const novos = Array.from(e.target.files);
    setArquivos(prev => [...prev, ...novos]);
    e.target.value = '';
  };

  const removerArquivo = (idx) => {
    setArquivos(prev => prev.filter((_, i) => i !== idx));
  };

  // Edição manual
  const abrirEdicao = () => {
    setDraft({
      apelido: profile?.apelido || '',
      como_agir: profile?.como_agir || '',
      falas: profile?.falas ? [...profile.falas] : [],
      familia: profile?.familia ? [...profile.familia] : [],
      historico_compras: profile?.historico_compras ? [...profile.historico_compras] : [],
      contexto: profile?.contexto ? [...profile.contexto] : [],
      pagamento: profile?.pagamento || '',
      alerta: profile?.alerta || '',
    });
    setEditMode(true);
  };

  const cancelarEdicao = () => {
    setEditMode(false);
    setDraft(null);
  };

  const salvarEdicao = async () => {
    setSaving(true);
    try {
      const payload = {
        apelido: draft.apelido || undefined,
        como_agir: draft.como_agir || undefined,
        falas: draft.falas,
        familia: draft.familia.filter(m => m.relacao),
        contexto: draft.contexto,
        pagamento: draft.pagamento || undefined,
        alerta: draft.alerta || null,
      };

      const res = await fetch(`/api/crm/profiles/contact/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      const data = await res.json();
      setProfile(data.profile);
      setEditMode(false);
      setDraft(null);
    } catch (err) {
      console.error('Erro ao salvar perfil:', err);
      alert('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cp-section">
        <span className="cp-label">Perfil do cliente</span>
        <div className="cp-loading">Carregando...</div>
      </div>
    );
  }

  if (error) return null;

  const temPerfilNovo = profile && (
    profile.como_agir || profile.apelido ||
    profile.falas?.length || profile.historico_compras?.length
  );

  if (!temPerfilNovo) {
    return (
      <div className="cp-wrapper">
        <div className="cp-section">
          <span className="cp-label">Perfil do cliente</span>
          <div className="cp-empty">
            {generating
              ? <p>Gerando perfil automaticamente...</p>
              : <p>Perfil ainda não gerado.</p>
            }
            {!generating && (
              <button className="cp-btn-gerar" onClick={handleGerar} disabled={generating}>
                ✦ Gerar perfil
              </button>
            )}
          </div>
        </div>
        <div className="cp-block-section">
          <button
            className={`cp-btn-block ${isBlocked ? 'cp-btn-block--blocked' : ''}`}
            onClick={handleToggleBlock}
            disabled={blocking}
          >
            {blocking ? '...' : isBlocked ? 'Desbloquear contato' : 'Bloquear contato'}
          </button>
        </div>
      </div>
    );
  }

  const geradoEm = profile.profile_generated_at
    ? new Date(profile.profile_generated_at).toLocaleDateString('pt-BR')
    : null;

  const nomeExibicao = profile.apelido || 'contato';
  const dados = editMode ? draft : profile;

  return (
    <div className="cp-wrapper">
      {/* Header */}
      <div className="cp-header">
        <div className="cp-header-left">
          {editMode ? (
            <input
              className="cp-edit-input cp-edit-apelido"
              value={draft.apelido}
              onChange={e => setDraft(d => ({ ...d, apelido: e.target.value }))}
              placeholder="Apelido do cliente"
            />
          ) : (
            <span className="cp-titulo">
              {profile.apelido || 'Perfil do cliente'}
            </span>
          )}
          {geradoEm && !editMode && (
            <span className="cp-gerado-em">atualizado {geradoEm}</span>
          )}
        </div>
        <div className="cp-header-actions">
          {editMode ? (
            <>
              <button
                className="cp-btn-salvar"
                onClick={salvarEdicao}
                disabled={saving}
                title="Salvar alterações"
              >
                {saving ? '...' : '✓'}
              </button>
              <button
                className="cp-btn-cancelar"
                onClick={cancelarEdicao}
                disabled={saving}
                title="Cancelar"
              >
                ✕
              </button>
            </>
          ) : (
            <>
              <button
                className="cp-btn-editar"
                onClick={abrirEdicao}
                title="Editar perfil manualmente"
              >✏️</button>
              <button
                className="cp-btn-anexar"
                onClick={() => fileInputRef.current?.click()}
                disabled={generating}
                title="Anexar documentos"
              >📎</button>
              <button
                className="cp-btn-atualizar"
                onClick={handleGerar}
                disabled={generating}
                title="Atualizar perfil"
              >
                {generating ? '...' : '↻'}
              </button>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.txt,.xlsx"
          style={{ display: 'none' }}
          onChange={handleArquivos}
        />
      </div>

      {/* Arquivos selecionados */}
      {arquivos.length > 0 && (
        <div className="cp-arquivos">
          {arquivos.map((f, i) => (
            <div key={i} className="cp-arquivo">
              <span className="cp-arquivo-nome">{f.name}</span>
              <button className="cp-arquivo-remove" onClick={() => removerArquivo(i)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Alerta */}
      <Alerta
        texto={dados.alerta}
        editMode={editMode}
        onChange={v => setDraft(d => ({ ...d, alerta: v }))}
      />

      {/* Como agir */}
      <ComoAgir
        texto={dados.como_agir}
        editMode={editMode}
        onChange={v => setDraft(d => ({ ...d, como_agir: v }))}
      />

      {/* Falas */}
      <Falas
        falas={dados.falas}
        editMode={editMode}
        onChange={v => setDraft(d => ({ ...d, falas: v }))}
      />

      {/* Família */}
      <Familia
        membros={dados.familia}
        editMode={editMode}
        onChange={v => setDraft(d => ({ ...d, familia: v }))}
      />

      {/* Últimas compras — somente leitura (fonte: sistema) */}
      <Compras historico={profile.historico_compras} />

      {/* Contexto */}
      <Contexto
        tags={dados.contexto}
        editMode={editMode}
        onChange={v => setDraft(d => ({ ...d, contexto: v }))}
      />

      {/* Pagamento */}
      {(editMode || profile.pagamento) && (
        <div className="cp-section">
          <span className="cp-label">Pagamento</span>
          {editMode ? (
            <input
              className="cp-edit-input"
              value={draft.pagamento}
              onChange={e => setDraft(d => ({ ...d, pagamento: e.target.value }))}
              placeholder="Forma de pagamento preferida..."
            />
          ) : (
            <p className="cp-pagamento">{profile.pagamento}</p>
          )}
        </div>
      )}

      {/* Bloquear contato */}
      <div className="cp-block-section">
        <button
          className={`cp-btn-block ${isBlocked ? 'cp-btn-block--blocked' : ''}`}
          onClick={handleToggleBlock}
          disabled={blocking}
        >
          {blocking
            ? '...'
            : isBlocked
              ? `Desbloquear ${nomeExibicao}`
              : `Bloquear ${nomeExibicao}`}
        </button>
      </div>
    </div>
  );
}

export default ClientProfile;
